import { AnalyticsUserProperty } from './analytics';

// Pure mapping from app state to the GA4 user properties we set. Kept platform-agnostic
// so web (App effect) and mobile (_layout effect) derive identical values from the shared
// catalogue. The effects own the side effect (setUserProperty); this module owns the rules.
//
//   inputs ──► buildAnalyticsUserProperties ──► [{name, value}] ──► setUserProperty (per item)
//
// Consent/opt-out gating lives in the platform analytics services, not here.

export type AnalyticsPlatform = 'web' | 'ios' | 'android';
export type SubscriptionStatus = 'free' | 'trial' | 'premium';

export interface SubscriptionSnapshot {
  /** Has the premium entitlement (paid). Mutually exclusive with isInTrial by construction. */
  isPremium: boolean;
  /** In the no-card 7-day trial (has trialStartedAt, not yet entitled, within window). */
  isInTrial: boolean;
  /** Entitlement state not yet resolved — caller should not write a value yet. */
  isLoading: boolean;
}

/**
 * free / trial / premium, or null while the entitlement is still loading (write nothing
 * rather than a misleading "free" on a premium user mid-hydration).
 */
export function resolveSubscriptionStatus(s: SubscriptionSnapshot): SubscriptionStatus | null {
  if (s.isLoading) return null;
  if (s.isInTrial) return 'trial';
  if (s.isPremium) return 'premium';
  return 'free';
}

export interface AnalyticsUserPropertyInputs {
  isLoggedIn: boolean;
  /** The platform this client runs on — drives last_platform. */
  platform: AnalyticsPlatform;
  subscription: SubscriptionSnapshot;
  /** Platform the account was created on (read from the user doc); null for legacy users. */
  signupPlatform: string | null;
}

/**
 * Returns the user properties to set for the current state. Skips values that should not be
 * written (unresolved subscription, missing signup platform). last_platform is set for everyone;
 * subscription_status and signup_platform require a logged-in user.
 */
export function buildAnalyticsUserProperties(
  input: AnalyticsUserPropertyInputs,
): Array<{ name: string; value: string }> {
  const props: Array<{ name: string; value: string }> = [
    { name: AnalyticsUserProperty.lastPlatform, value: input.platform },
  ];
  if (!input.isLoggedIn) return props;

  const status = resolveSubscriptionStatus(input.subscription);
  if (status) props.push({ name: AnalyticsUserProperty.subscriptionStatus, value: status });
  if (input.signupPlatform) {
    props.push({ name: AnalyticsUserProperty.signupPlatform, value: input.signupPlatform });
  }
  return props;
}
